import { SelectInput, SelectInputItem, Switch, TextField, Tooltip } from '@ads/components-react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { RichTextEditor, Slider } from '@/shared/ui/atoms';
import { useExamFormOptions } from '../../../hooks/useExamFormOptions';
import type { ExamFormData } from '../../../types/exam';
import { CompanyAutocomplete } from '../../molecules/CompanyAutocomplete';
import { DisciplineAutocomplete } from '../../molecules/DisciplineAutocomplete';

export interface ExamFormProps {
  readonly form: ExamFormData;
  readonly onUpdate: (partial: Partial<ExamFormData>) => void;
  /**
   * Exibe o campo "Instruções da prova". Padrão: `true`.
   *
   * O fluxo de edição passa `false`: o GET `/exam/grid/{id}` não retorna o
   * `instruction`, então o campo não pode ser pré-preenchido e salvá-lo
   * sobrescreveria o valor existente com vazio. Na criação (wizard) o campo
   * permanece visível.
   */
  readonly showInstruction?: boolean;
}

// ─── Sub-componente: linha de switch (label à esquerda, toggle à direita) ─────

function SwitchRow({
  label,
  checked,
  onCheckedChange,
  info,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  info?: string;
}) {
  return (
    <div className="flex items-center gap-micro border-t-hairline border-ctx-layout-border px-mini py-micro first:border-t-0">
      <Switch checked={checked} size="sm" onCheckedChange={onCheckedChange} />
      <span className="ts-paragraph-xs text-ctx-content-title">{label}</span>
      {info && (
        <Tooltip title={info}>
          <InformationCircleIcon className="size-xxxs text-ctx-content-base" />
        </Tooltip>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function ExamForm({ form, onUpdate, showInstruction = true }: ExamFormProps) {
  const { t } = useTranslation('examManagement');
  const { examTypes, gradeCriteriaOptions, referenceShowOptions } = useExamFormOptions();

  return (
    <div className="flex flex-col gap-mini">
      {/* ── Título ─────────────────────────────────────────────────────────── */}
      <TextField
        label={t('form.fields.title')}
        placeholder={t('form.fields.titlePlaceholder')}
        value={form.title}
        fullWidth
        onChange={e => onUpdate({ title: e.target.value })}
        size="md"
      />

      {/* ── Tipo | Empresa ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-mini md:grid-cols-2">
        <SelectInput
          label={t('form.fields.examType')}
          placeholder={t('form.fields.examTypePlaceholder')}
          value={form.examType !== null ? String(form.examType) : ''}
          onValueChange={v => onUpdate({ examType: v ? Number(v) : null })}
          size="md"
          fullWidth
        >
          {examTypes.map(item => (
            <SelectInputItem
              key={item.key}
              value={String(item.key ?? '')}
              custom={{ root: 'text-nowrap' }}
            >
              {item.value ?? ''}
            </SelectInputItem>
          ))}
        </SelectInput>
        <CompanyAutocomplete
          value={form.companyId}
          selectedLabel={form.companyName ?? ''}
          onChange={(id, label) => onUpdate({ companyId: id, companyName: label })}
          label={t('form.fields.company')}
          placeholder={t('form.fields.companyPlaceholder')}
        />
      </div>

      {/* ── Disciplina ─────────────────────────────────────────────────────── */}
      <DisciplineAutocomplete
        label={t('form.fields.discipline')}
        placeholder={t('form.fields.disciplinePlaceholder')}
        value={form.disciplineId}
        selectedLabel={form.disciplineName ?? ''}
        onChange={(id, name) => onUpdate({ disciplineId: id, disciplineName: name || null })}
      />

      {/* ── Instruções da prova ────────────────────────────────────────────── */}
      {showInstruction && (
        <div className="flex flex-col gap-nano">
          <span className="ts-heading-xxs !font-medium text-ctx-content-title">
            {t('form.fields.instruction')}
          </span>
          <RichTextEditor
            placeholder={t('form.fields.instructionPlaceholder')}
            value={form.instruction}
            onChange={value => onUpdate({ instruction: value })}
            minHeight={120}
          />
        </div>
      )}

      {/* ── Critério de nota | Quantidade de perguntas | Peso ──────────────── */}
      <div className="grid grid-cols-1 gap-mini md:grid-cols-3">
        <SelectInput
          label={t('form.fields.gradeCriteria')}
          placeholder={t('form.fields.gradeCriteria')}
          value={form.gradeCriteria !== null ? String(form.gradeCriteria) : ''}
          onValueChange={v => onUpdate({ gradeCriteria: v ? Number(v) : null })}
          size="md"
          fullWidth
        >
          {gradeCriteriaOptions.map(item => (
            <SelectInputItem
              key={item.key}
              value={String(item.key ?? '')}
              custom={{ root: 'text-nowrap' }}
            >
              {item.value ?? ''}
            </SelectInputItem>
          ))}
        </SelectInput>
        <TextField
          label={t('form.fields.questionsQuantity')}
          type="number"
          min={1}
          placeholder={t('form.fields.questionsQuantity')}
          fullWidth
          value={form.questionsQuantity !== null ? String(form.questionsQuantity) : ''}
          onChange={e =>
            onUpdate({
              questionsQuantity: e.target.value ? Number(e.target.value) : null,
            })
          }
          size="md"
        />
        <TextField
          label={t('form.fields.weight')}
          type="number"
          min={1}
          max={2}
          fullWidth
          value={String(form.weight)}
          onChange={e => {
            const parsed = Number(e.target.value);
            const next = Number.isNaN(parsed) ? 1 : Math.min(2, Math.max(1, parsed));
            onUpdate({ weight: next });
          }}
          size="md"
        />
      </div>

      {/* ── Visualização antes | Visualização após | Percentual ───────────── */}
      <div className="grid grid-cols-1 gap-mini md:grid-cols-3">
        <SelectInput
          label={t('form.fields.examReferenceShow')}
          placeholder={t('form.fields.examReferenceShow')}
          value={form.examReferenceShow !== null ? String(form.examReferenceShow) : ''}
          onValueChange={v => onUpdate({ examReferenceShow: v ? Number(v) : null })}
          size="md"
          fullWidth
        >
          {referenceShowOptions.map(item => (
            <SelectInputItem
              key={item.key}
              value={String(item.key ?? '')}
              custom={{ root: 'text-nowrap' }}
            >
              {item.value ?? ''}
            </SelectInputItem>
          ))}
        </SelectInput>
        <SelectInput
          label={t('form.fields.examReferenceShowPost')}
          placeholder={t('form.fields.examReferenceShowPost')}
          value={form.examReferenceShowPost !== null ? String(form.examReferenceShowPost) : ''}
          onValueChange={v => onUpdate({ examReferenceShowPost: v ? Number(v) : null })}
          size="md"
          fullWidth
        >
          {referenceShowOptions.map(item => (
            <SelectInputItem
              key={item.key}
              value={String(item.key ?? '')}
              custom={{ root: 'text-nowrap' }}
            >
              {item.value ?? ''}
            </SelectInputItem>
          ))}
        </SelectInput>

        {/* Percentual de complexidade */}
        <div className="flex flex-col gap-nano">
          <div className="flex items-center justify-between gap-nano">
            <span className="ts-heading-xxs !font-medium text-ctx-content-title">
              {t('form.fields.percentualComplexity')}
            </span>
            <span className="ts-subtitle-xxs text-ctx-content-title">
              {form.percentualComplexity}%
            </span>
          </div>
          <div className="flex flex-1 items-center">
            <div className="w-full">
              <Slider
                value={form.percentualComplexity}
                onChange={e => onUpdate({ percentualComplexity: Number(e.target.value) })}
                min={0}
                max={100}
                step={1}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Configurações (lista de switches) ──────────────────────────────── */}
      <div className="overflow-hidden rounded-md border-hairline border-ctx-layout-border">
        <SwitchRow
          label={t('form.fields.randomize')}
          checked={form.randomize}
          onCheckedChange={checked => onUpdate({ randomize: checked })}
        />
        <SwitchRow
          label={t('form.fields.showAnswersTries')}
          checked={form.showAnswersTries}
          onCheckedChange={checked => onUpdate({ showAnswersTries: checked })}
        />
        <SwitchRow
          label={t('form.fields.enableSafeEvaluationMode')}
          checked={form.enableSafeEvaluationMode}
          onCheckedChange={checked => onUpdate({ enableSafeEvaluationMode: checked })}
        />
        <SwitchRow
          label={t('form.fields.recommendedToDoOnPortal')}
          checked={form.recommendedToDoOnPortal}
          onCheckedChange={checked => onUpdate({ recommendedToDoOnPortal: checked })}
          info={t('form.fields.recommendedToDoOnPortalTooltip')}
        />
        <SwitchRow
          label={t('form.fields.enableBlockUnauthorizedActions')}
          checked={form.enableBlockUnauthorizedActions}
          onCheckedChange={checked => onUpdate({ enableBlockUnauthorizedActions: checked })}
        />

        {/* Saídas de tela permitidas — sub-configuração dependente do bloqueio de eventos */}
        {form.enableBlockUnauthorizedActions && (
          <div className="flex items-center gap-mini border-t-hairline border-ctx-layout-border bg-ctx-layout-body px-mini py-micro">
            <span className="ts-paragraph-xxs text-ctx-content-base">
              {t('form.fields.maxScreenExitsAllowed')}
            </span>
            <div className="w-[6rem]">
              <TextField
                aria-label={t('form.fields.maxScreenExitsAllowed')}
                type="number"
                min={0}
                fullWidth
                value={String(form.maxScreenExitsAllowed)}
                onChange={e => onUpdate({ maxScreenExitsAllowed: Number(e.target.value) })}
                size="sm"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
